import { useEffect, useState } from 'react';
import type { Route } from '../design/schema';
import { formatDuration } from '../design/stats';
import { connectStrava, importActivity, listActivities, type StravaActivity } from '../services/strava';
import { Modal } from './ui';

/** Connect to Strava, browse recent activities and add them to the design. */
export default function StravaPicker({ clientId, initial, onAdd, onClose }: {
  clientId: string;
  /** The sign-in already started by the click that opened this picker. */
  initial: Promise<string>;
  onAdd: (route: Route) => void;
  onClose: () => void;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [activities, setActivities] = useState<StravaActivity[]>([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(true);
  const [busy, setBusy] = useState<'connect' | 'list' | number | null>('connect');
  const [added, setAdded] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) => {
    const message = e instanceof Error ? e.message : 'Something went wrong.';
    if (message === 'CANCELLED') return onClose();
    setError(message);
  };

  const follow = (attempt: Promise<string>) => {
    setError(null);
    setBusy('connect');
    attempt.then((t) => setToken(t), fail).finally(() => setBusy(null));
  };
  // Retry opens the Strava window straight from the click, which is what browsers require.
  const connect = () => follow(connectStrava(clientId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => follow(initial), []);

  useEffect(() => {
    if (!token) return;
    setBusy('list');
    listActivities(token, page)
      .then((list) => {
        setActivities((prev) => [...prev, ...list]);
        setMore(list.length > 0);
      }, fail)
      .finally(() => setBusy(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page]);

  const add = (a: StravaActivity) => {
    if (!token) return;
    setBusy(a.id);
    setError(null);
    importActivity(token, a)
      .then((route) => {
        onAdd(route);
        setAdded((s) => new Set(s).add(a.id));
      }, fail)
      .finally(() => setBusy(null));
  };

  return (
    <Modal title="Add from Strava" onClose={onClose}>
      {error && (
        <div className="tmd-alert" role="alert">
          <p>{error}</p>
          {!token && (
            <button type="button" className="tmd-linkbtn" onClick={connect}>
              Try again
            </button>
          )}
        </div>
      )}
      {!token && busy === 'connect' && <p className="tmd-muted">Waiting for you to approve access in the Strava window…</p>}
      {token && (
        <>
          <ul className="tmd-activities">
            {activities.map((a) => (
              <li key={a.id}>
                <span className="tmd-route-main">
                  <strong>{a.name}</strong>
                  <em>
                    {new Date(a.start_date_local).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {(a.distance / 1000).toFixed(1)} km ·{' '}
                    {Math.round(a.total_elevation_gain)} m · {formatDuration(a.moving_time)}
                  </em>
                </span>
                <button type="button" className="tmd-btn tmd-btn-small" disabled={busy === a.id || added.has(a.id)} onClick={() => add(a)}>
                  {added.has(a.id) ? 'Added' : busy === a.id ? 'Adding…' : 'Add'}
                </button>
              </li>
            ))}
          </ul>
          {busy === 'list' && <p className="tmd-muted">Loading activities…</p>}
          {busy !== 'list' && !activities.length && <p className="tmd-muted">No activities with a GPS track were found.</p>}
          {more && activities.length > 0 && busy !== 'list' && (
            <button type="button" className="tmd-btn tmd-btn-ghost" onClick={() => setPage((p) => p + 1)}>
              Load older activities
            </button>
          )}
          <button type="button" className="tmd-btn" onClick={onClose}>
            Done
          </button>
        </>
      )}
    </Modal>
  );
}
